import {
  filter,
  filterSheetButton,
  filterSheetFooterTemplate,
  filterSheetTemplate,
  filterTitles,
  localNavigation,
  menuBarButtonForm,
  selectedNavigationTitle,
} from "./Selectors";
import { setActiveConfiguration, setOpenFilterIds } from "@otto-ec/heureka-components/filter";
import {
  clearEphemeralFirstLevelValues,
  commitEphemeralOrder,
  savePendingEphemeralOrder,
} from "@otto-ec/heureka-components/facet";
import { FilterTitle } from "../filter/FilterTitle";
import { Filter } from "../filter/Filter";
import { FIRST_LEVEL_CHANGE_TRACKING_LABELS, SIDEBAR_CHANGE_TRACKING_LABELS } from "./FilterSheet";
import { FacetValueTag } from "../filter/FacetValueTag";
import { isSelected, SvelteFacetValue } from "../filter/SvelteFacetValue";
import { RefinementBar } from "../refinementBar/RefinementBar";
import type { TrackingLabels } from "../tracking/TrackingLabels";
import { FilterAccordion } from "../filter/FilterAccordion";
import { forEachElement, moveAllChildren } from "../util/Utils";
import { FilterSection } from "../filter/FilterSection";
import { FilterTitles } from "../filter/FilterTitles";
import { type OcSheetV1Events, sheetV1 } from "@otto-ec/otto-components/sheet";
import { HeurekaElementFactory } from "../util/HeurekaElementFactory";
import { eventQBus } from "../types/EventQBus";
import { registerClickTracker, sanFoldout } from "./FilterSheetTracking";
import { readLegacyLabel } from "../tracking/FeatureTracking";
import { readSanPopularFacetValues, updateSanPopularFacetValues } from "../filter/SveltePopularFacetValues";
import PartialLock from "../util/PartialLock";
import { isActive } from "../toggle/Toggle";
import { hasChanges } from "../filter/FilterFormActions";
import { readSanTopQuickFacet, updateSanTopQuickFacet } from "../nextBestAction/NextBestActionComponent";

const FILTER_SHEET_ID = "heureka-filtersheet";
const FILTER_SHEET_CONTENT_CLASS = "heureka_filterSheet__content";
const FILTER_SHEET_ACTIONS_CLASS = "heureka_filterSheet__actions";
const FILTER_SHEET_CONTENT_SELECTOR = `.${FILTER_SHEET_CONTENT_CLASS}`;
const FILTER_SHEET_ACTIONS_SELECTOR = `.${FILTER_SHEET_ACTIONS_CLASS}`;
const FILTER_SHEET_LOADING_CLASS = `${FILTER_SHEET_CONTENT_CLASS}--loading`;

export default class OcFilterSheet {
  static readonly factory = HeurekaElementFactory.byId(FILTER_SHEET_ID, OcFilterSheet);

  /*               */

  constructor(
    readonly sheet?: sheetV1,
    private readonly sheetContent: HTMLElement | null | undefined = sheet?.querySelector(FILTER_SHEET_CONTENT_SELECTOR),
    private readonly sheetActions: HTMLElement | null | undefined = sheet?.querySelector(FILTER_SHEET_ACTIONS_SELECTOR),
  ) {}

  public static pick() {
    return OcFilterSheet.factory.pick();
  }

  public static declare(): OcFilterSheet | undefined {
    const sheet = OcFilterSheet.factory.byId(FILTER_SHEET_ID);
    return sheet?.sheet?.open ? sheet : undefined;
  }

  public static savePopularValuesOrder(event?: Event) {
    if (event) {
      const target = event.target;
      if (!(target instanceof HTMLElement && target.classList.contains("hc_facetValueInput"))) return;
      const scrollBox = target.closest(".hc_facet__scrollBox");
      if (!scrollBox) return;
      const filterId = target.closest("form[id]")?.id?.replace("find_filter_", "");
      if (!filterId) return;

      const values: string[] = [];
      scrollBox.querySelectorAll<HTMLInputElement>(".hc_facetValueInput").forEach((input) => {
        if (input.value) values.push(input.value);
      });
      savePendingEphemeralOrder(filterId, values);
    }
  }

  public static commitPopularValuesOrder() {
    commitEphemeralOrder();
  }

  public static dropPopularValuesOrder() {
    clearEphemeralFirstLevelValues();
  }

  /*       */

  setSheet() {
    this.sheetContent?.appendChild(filterTitles());
    return this;
  }

  initializeSheet(updateChangeTracking = true) {
    const content = this.sheetContent;
    if (content) {
      if (isActive("HEUREKA_2288_LAZY_FILTER_LOADING")) {
        setOpenFilterIds([]);
        try {
          performance.mark("mobileFilterMount:start");
        } catch {
          /*       */
        }
        setTimeout(() => {
          try {
            performance.mark("mobileFilterMount:backgroundStart");
          } catch {
            /*       */
          }
          setOpenFilterIds(FilterAccordion.factory.all().map((a) => a.filterId));
          requestAnimationFrame(() => {
            try {
              performance.measure("mobileFilterMount:backgroundRender", "mobileFilterMount:backgroundStart");
              performance.measure("mobileFilterMount:total", "mobileFilterMount:start");
            } catch {
              /*       */
            }
          });
        }, 0);
      }
      setActiveConfiguration("sheetFirstLevel");
      updateSanPopularFacetValues(content, readSanPopularFacetValues());
      updateSanTopQuickFacet(content, readSanTopQuickFacet());

      OcFilterSheet.moveDesktopInlinedFiltersToSheet(content, updateChangeTracking);
      moveAllChildren(filterSheetFooterTemplate(), this.sheetActions, true);

      commitEphemeralOrder();
      const titles = filterTitles();
      if (titles) {
        titles.removeEventListener("change", OcFilterSheet.savePopularValuesOrder, { capture: true });
        titles.addEventListener("change", OcFilterSheet.savePopularValuesOrder, { capture: true });
      }

      /*                                                                 */
      /*                                                                            */
      menuBarButtonForm()?.addEventListener(
        "click",
        () => {
          FacetValueTag.factory.forEach((tag) => {
            tag.updateTrackingLabelsOnReferenced("deleted", "x_all_local", "first_level");
          });
          SvelteFacetValue.factory
            .all(RefinementBar.facetValueTagsElement())
            .filter(isSelected)
            .forEach((tag) => {
              tag.updateTrackingLabelsOnReferenced("deleted", "x_all_local", "first_level");
            });
        },
        { capture: true },
      );
    }
    return this;
  }

  /*                       */

  deactivate() {
    const source = this.sheetContent;
    if (source) {
      OcFilterSheet.closeAllTooltips(source);
      OcFilterSheet.fireDelayedActionsNowOrNever();
      OcFilterSheet.moveInlinedFiltersFrom(source, true, SIDEBAR_CHANGE_TRACKING_LABELS);
      clearEphemeralFirstLevelValues();
      moveAllChildren(source, filterSheetTemplate(), true);
    }
    if (this.sheetActions) {
      moveAllChildren(this.sheetActions, filterSheetFooterTemplate(), true);
    }
  }

  public lockContent() {
    if (this.sheet) {
      this.sheet?.classList.add(FILTER_SHEET_LOADING_CLASS);
    }
    return this;
  }

  public unlockContent() {
    this.sheet?.classList.remove(FILTER_SHEET_LOADING_CLASS);
    return this;
  }

  /*                          */
  public updateContent() {
    this.sheetContent.innerHTML = "";
    this.setSheet().initializeSheet();
    this.sheetActions?.replaceChildren();
    moveAllChildren(filterSheetFooterTemplate(), this.sheetActions, true);
    return this;
  }

  public close() {
    if (this.sheet) {
      this.deactivate();
      this.sheet.close();
    }
    return this;
  }

  public open() {
    if (this.sheet) {
      this.sheet.open = true;
    }
    return this;
  }

  public setScrollOffset(offset?: number) {
    if (typeof offset === "number") {
      const content: HTMLElement = this.sheet as HTMLElement;
      if (content) {
        content.scrollTop = offset;
      }
    }
    return this;
  }

  /*       */
  static moveDesktopInlinedFiltersToSheet(content: HTMLElement, updateChangeTracking: boolean) {
    FilterTitle.factory.forEach((filterTitle) => {
      if (updateChangeTracking) {
        Filter.updateChangeTrackingLabels(filterTitle.filterId, FIRST_LEVEL_CHANGE_TRACKING_LABELS);
      }
      filterTitle.attachFilter();
    });
  }

  public static scrollToRecentFilter(recentFilterId: string) {
    if (recentFilterId) {
      const filterSheetFooterRect = OcFilterSheet.pick()
        .sheet?.shadowRoot?.querySelector(".sheet__footer")
        .getBoundingClientRect();
      const filterTitle = Filter.filterId(recentFilterId)?.filterTitle?.elem;
      const filterTitleRect = filterTitle.getBoundingClientRect();

      if (OcFilterSheet.isHiddenByFooter(filterTitleRect, filterSheetFooterRect) && filterTitle.scrollIntoView) {
        filterTitle.scrollIntoView(true);
      }
    }
  }

  static isHiddenByFooter(titleRect: DOMRect, footerRect: DOMRect): boolean {
    return titleRect && footerRect && titleRect.bottom > footerRect.top;
  }

  static moveInlinedFiltersFrom(
    content: HTMLElement,
    updateChangeTracking: boolean,
    trackingLabel: Partial<TrackingLabels>,
  ) {
    FilterTitle.factory.forEach((filterTitle) => {
      if (updateChangeTracking) {
        Filter.updateChangeTrackingLabels(filterTitle.filterId, trackingLabel);
      }
      filterTitle.detachFilter();
    });
  }

  public static addLocalNavigationTitleValue() {
    const title = selectedNavigationTitle(localNavigation());
    const titles = filterTitles();
    if (titles && title) {
      const { overlayEntryTitle = "" } = title.dataset;
      forEachElement(".js_selectedNavigationFilterSheet", (elem) => (elem.innerText = overlayEntryTitle), titles);
    }
  }

  public static addInitialLoadError() {
    const errorMessage = FilterSection.template()?.errorMessage;
    if (errorMessage) {
      FilterTitles.template()?.elem.append(errorMessage.cloneNode(true));
    }
  }

  public addListeners() {
    if (this.sheet) {
      /*         */
      this.sheet?.addEventListener("oc-sheet-before-open", (event: OcSheetV1Events["oc-sheet-before-open"]) => {
        /*                                                                                       */
        /*                                                                                                                            */
        /*                                                                                                             */
        const hasActiveSheet = !!document.querySelector("oc-sheet-v1[open]");
        if (event.detail.action === "user-agent" && !hasActiveSheet) {
          event.preventDefault();
          window.history.replaceState({}, "", window.location.pathname + window.location.search);
          return;
        }
        this.setSheet().initializeSheet();
      });

      this.sheet?.addEventListener("oc-sheet-after-open", () => {
        eventQBus.emit("ftfind.filterSheet.opened", { filterId: undefined });
      });

      /*          */
      this.sheet?.addEventListener("oc-sheet-before-close", (event: OcSheetV1Events["oc-sheet-close"]) => {
        /*                                                     */
        /*                                                                                             */
        if (event.detail.closeType === "closedWithUserAgent") {
          event.preventDefault();
          return;
        }

        if (this.sheet?.classList.contains(FILTER_SHEET_LOADING_CLASS)) {
          event.preventDefault();
          return;
        }

        /*                                                                                    */
        /*                                                             */
        /*                                                                               */
        window.history.replaceState({}, "", window.location.pathname + window.location.search);

        /*                                                                                     */
        if (event.detail.closeType !== "closedProgrammatically") {
          this.deactivate();
        }
      });
      this.sheet?.addEventListener("oc-sheet-after-close", (event) => {
        const { tsLabels = "{}" } = this.sheetContent?.dataset || {};
        const san_FilterWhatIf = readLegacyLabel(filterSheetButton(), "san_FilterWhatIf");
        if (event.detail.closeType !== "closedProgrammatically") {
          eventQBus.emit("ftfind.filterSheet.closed", {
            filterId: undefined,
            tracking: {
              san_Foldout: sanFoldout(event.detail.closeType),
              san_FilterWhatIf,
              ...JSON.parse(tsLabels),
            },
          });
        }
      });

      registerClickTracker(this.sheet, {
        capture: true,
        passive: true,
      });
    }
  }

  public static initPartialLocking() {
    const titles = filterTitles();
    if (titles) {
      PartialLock.create(
        titles,
        ".find_filterTitles__item, .heureka_filterTitle",
        (lockOwner) => {
          const filterId = lockOwner.dataset.filterId;
          const filterForm = filterId && filter(filterId);
          if (filterForm) {
            filterForm.delayedAction?.nowOrNever();
            return hasChanges(filterForm);
          }
          return false;
        },
        {
          unlockTopics: [
            "heureka.filterSection.loadAborted",
            "ftfind.dresonRule.resolveAborted",
            "ftfind.dresonRule.resolved",
          ],
        },
      );
    }
  }

  public static addErrorsOnFilterTitleValues(filterIds?: string[]) {
    const titles = filterTitles();
    if (titles) {
      const invalidIds = filterIds || FilterAccordion.invalidFilterIds();

      invalidIds.forEach((filterId) =>
        forEachElement(
          `.find_filterTitles__item[data-filter-id="${filterId}"]`,
          (item) => item.classList.add("find_filterTitles__item--invalid"),
          titles,
        ),
      );
    }
  }

  private static closeAllTooltips(sheetContent: HTMLElement) {
    /*                                                                                          */
    forEachElement(
      ".pl_tooltip--bottom .pl_icon",
      (tooltip) => tooltip.dispatchEvent(new Event("click", { bubbles: true })),
      sheetContent,
    );
  }

  public static fireDelayedActionsNowOrNever() {
    const titles = filterTitles();
    if (titles) {
      FilterTitle.factory.forEach((filterTitle) => filterTitle.filter?.form?.delayedAction?.nowOrNever());
    }
  }
}
